/**
 * Modules loader
 *
 * Load non-critical module, i.e. JS lib that needed by some (but not all) components.
 * @example
 * import utils from '../utils';
 * utils.modules.load('splide').then(Splide => {
 *   // Splide is the imported module
 * }).catch(err => {
 *   console.error(err);
 * })
 */
import {EventEmitter} from '@billjs/event-emitter';

/**
 * Keep loaded modules list
 *
 * @type Object
 */
const loadedModules = {};

/**
 * Available modules list and how to import them.
 *
 * @type Object
 */
const availableModules = {
  'splide': () => {
    return import('@splidejs/splide');
  },
  'plyr': () => {
    return import('plyr');
  },
  'handlebars': {
    'import': () => {
      return import('handlebars/dist/handlebars.js');
    },
    'afterImport': (TheModule) => {
      return import('./handlebars')
        .then(({default: __}) => {
          __.setup(TheModule);
        });
    }
  } 
};

/**
 * List of individual module loader.
 * @type {Array <EventEmitter>}
 */
const loaders = {};

/**
 * Load a module
 *
 * @param {String} moduleName Module Name
 * @returns
 */
const loadModule = async (moduleName) => {
  if (typeof availableModules[moduleName] === 'undefined') {
    throw new Error(`${moduleName} is not available`);
  }

  if (typeof loadedModules[moduleName] !== 'undefined') {
    return loadedModules[moduleName];
  }

  const _module = availableModules[moduleName];
  let importer, afterImport;

  if (typeof _module === 'function') {
    importer = _module;
  }
  else if (typeof _module['import'] === 'function') {
    importer = _module['import'];
    afterImport = _module['afterImport'];
  }

  if (!importer) {
    throw new Error(`${moduleName} is not correctly defined.`);
  }
  
  var needLoad = false;

  if (typeof loaders[moduleName] === 'undefined') {
    loaders[moduleName] = new EventEmitter();
    needLoad = true;
  }

  const ret = new Promise((resolve, reject) => {
    loaders[moduleName].on('loaded', (ev) => {
      resolve(ev.data);
    });
    loaders[moduleName].on('error', (ev) => {
      reject(ev.data);
    });
  });

  if (needLoad) {
    try {
      importer()
        .then(({default: TheModule}) => {
          loadedModules[moduleName] = TheModule;

          if (typeof afterImport === 'function') {
            afterImport(TheModule).then(() => {
              window.setTimeout(() => {
                loaders[moduleName].fire('loaded', TheModule);
              });
            });
          }
          else {
            window.setTimeout(() => {
              loaders[moduleName].fire('loaded', TheModule);
            });
          }
        })
        .catch(err => {
          window.setTimeout(() => {
            loaders[moduleName].fire('error', err);
          });
        });
    }
    catch (err) {
      window.setTimeout(() => {
        loaders[moduleName].fire('error', err);
      });
    }
  }

  return ret;
};

/**
 * Get (or load if has not been loaded yet) a Module.
 *
 * @param {String} name Module Name
 * @returns
 */
export const getModule = async (name) => {
  return new Promise((resolve, reject) => {
    if (typeof availableModules[name] === 'undefined') {
      reject(new Error(`${name} is not available`));
      return;
    }

    loadModule(name)
      .then((TheModule) => {
        resolve(TheModule);
      })
      .catch(err => {
        reject(err);
      });
  });
};


export default {
  modules: {
    load: getModule
  }
};